/**
 * hook for keyboard navigation in the thumbnail panel with arrow keys
 * handles page selection, multi-select, and synchronized scrolling
 */
import { useHotkeys } from "react-hotkeys-hook";
import type { Virtualizer } from "@tanstack/react-virtual";

import { scrollToPage } from "../utils/virtualScrollUtils";

export const useThumbnailShortcuts = (
  focusedPage: number | null,
  setFocusedPage: (page: number) => void,
  selectedPages: Set<number>,
  onSelectionChange: (pages: Set<number>) => void,
  onPageSelect: (page: number) => void,
  pageCount: number,
  virtualizer: Virtualizer<HTMLDivElement, Element>,
  disableHotkeys: boolean,
  togglePageSelection: (page: number, isChecking: boolean) => void,
  isPageSelectionUnavailable: (page: number) => boolean = () => false,
  isSelectingMoveTarget: boolean = false,
  visiblePageNumbers?: number[],
) => {
  const availablePages =
    visiblePageNumbers && visiblePageNumbers.length > 0
      ? visiblePageNumbers
      : Array.from({ length: pageCount }, (_, index) => index + 1);

  const getPageIndex = (page: number) => availablePages.indexOf(page);
  const getPageAtIndex = (index: number): number | null =>
    availablePages[index] ?? null;

  // keyboard navigation for thumbnails
  // if any pages are selected, arrow keys will select/deselect as you navigate
  useHotkeys(
    "up",
    (e) => {
      e.preventDefault();

      if (focusedPage === null) {
        return;
      }

      const focusedIndex = getPageIndex(focusedPage);
      if (focusedIndex <= 0) {
        return;
      }

      const newPage = getPageAtIndex(focusedIndex - 1);
      if (newPage === null) {
        return;
      }

      if (selectedPages.size > 0) {
        const newSelection = new Set(selectedPages);

        if (!newSelection.has(newPage)) {
          if (!isPageSelectionUnavailable(newPage)) {
            newSelection.add(newPage);
          }
        } else if (newSelection.has(focusedPage)) {
          newSelection.delete(focusedPage);
        }

        onSelectionChange(newSelection);
      }

      setFocusedPage(newPage);
      onPageSelect(newPage);
      scrollToPage({
        pageIndex: focusedIndex - 1,
        totalPages: pageCount,
        virtualizer,
      });
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      selectedPages,
      onSelectionChange,
      onPageSelect,
      pageCount,
      virtualizer,
      disableHotkeys,
      isPageSelectionUnavailable,
      isSelectingMoveTarget,
      visiblePageNumbers,
    ],
  );

  useHotkeys(
    "down",
    (e) => {
      e.preventDefault();

      if (focusedPage === null) {
        return;
      }

      const focusedIndex = getPageIndex(focusedPage);
      if (focusedIndex === -1 || focusedIndex >= pageCount - 1) {
        return;
      }

      const newPage = getPageAtIndex(focusedIndex + 1);
      if (newPage === null) {
        return;
      }

      if (selectedPages.size > 0) {
        const newSelection = new Set(selectedPages);

        if (!newSelection.has(newPage)) {
          if (!isPageSelectionUnavailable(newPage)) {
            newSelection.add(newPage);
          }
        } else if (newSelection.has(focusedPage)) {
          newSelection.delete(focusedPage);
        }

        onSelectionChange(newSelection);
      }

      setFocusedPage(newPage);
      onPageSelect(newPage);
      scrollToPage({
        pageIndex: focusedIndex + 1,
        totalPages: pageCount,
        virtualizer,
      });
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      selectedPages,
      onSelectionChange,
      onPageSelect,
      pageCount,
      virtualizer,
      disableHotkeys,
      isPageSelectionUnavailable,
      isSelectingMoveTarget,
      visiblePageNumbers,
    ],
  );

  useHotkeys(
    "shift+up",
    (e) => {
      e.preventDefault();

      if (focusedPage === null) {
        return;
      }

      const focusedIndex = getPageIndex(focusedPage);
      if (focusedIndex <= 0) {
        return;
      }

      const newIndex = Math.max(focusedIndex - 10, 0);
      const newPage = getPageAtIndex(newIndex);
      if (newPage === null) {
        return;
      }

      if (selectedPages.size > 0) {
        const newSelection = new Set(selectedPages);
        const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
        const maxSelectedPage = sortedPages[sortedPages.length - 1];

        if (focusedPage === maxSelectedPage) {
          for (let index = focusedIndex; index > newIndex; index -= 1) {
            const page = getPageAtIndex(index);
            if (page !== null) {
              newSelection.delete(page);
            }
          }
        } else {
          for (let index = newIndex; index <= focusedIndex; index += 1) {
            const page = getPageAtIndex(index);
            if (page !== null && !isPageSelectionUnavailable(page)) {
              newSelection.add(page);
            }
          }
        }

        onSelectionChange(newSelection);
      }

      setFocusedPage(newPage);
      onPageSelect(newPage);
      virtualizer.scrollToIndex(newIndex, { align: "center" });
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      selectedPages,
      pageCount,
      virtualizer,
      disableHotkeys,
      isPageSelectionUnavailable,
      isSelectingMoveTarget,
      visiblePageNumbers,
    ],
  );

  useHotkeys(
    "shift+down",
    (e) => {
      e.preventDefault();

      if (focusedPage === null) {
        return;
      }

      const focusedIndex = getPageIndex(focusedPage);
      if (focusedIndex === -1 || focusedIndex >= pageCount - 1) {
        return;
      }

      const newIndex = Math.min(focusedIndex + 10, pageCount - 1);
      const newPage = getPageAtIndex(newIndex);
      if (newPage === null) {
        return;
      }

      if (selectedPages.size > 0) {
        const newSelection = new Set(selectedPages);

        for (let index = focusedIndex; index <= newIndex; index += 1) {
          const page = getPageAtIndex(index);
          if (page !== null && !isPageSelectionUnavailable(page)) {
            newSelection.add(page);
          }
        }

        onSelectionChange(newSelection);
      }

      setFocusedPage(newPage);
      onPageSelect(newPage);
      virtualizer.scrollToIndex(newIndex, { align: "center" });
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      selectedPages,
      pageCount,
      virtualizer,
      disableHotkeys,
      isPageSelectionUnavailable,
      isSelectingMoveTarget,
      visiblePageNumbers,
    ],
  );

  // left/right arrows: first/last page without affecting selection
  // disabled when there's an active selection
  useHotkeys(
    "left",
    (e) => {
      if (selectedPages.size > 0) {
        return;
      }

      e.preventDefault();
      const newPage = availablePages[0];

      if (focusedPage !== null && newPage !== undefined) {
        setFocusedPage(newPage);
        onPageSelect(newPage);
        virtualizer.scrollToIndex(0, { align: "center" });
      }
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      virtualizer,
      disableHotkeys,
      selectedPages,
      isSelectingMoveTarget,
      visiblePageNumbers,
    ],
  );

  useHotkeys(
    "right",
    (e) => {
      if (selectedPages.size > 0) {
        return;
      }

      e.preventDefault();
      const newPage = availablePages[availablePages.length - 1];

      if (focusedPage !== null && newPage !== undefined) {
        setFocusedPage(newPage);
        onPageSelect(newPage);
        virtualizer.scrollToIndex(availablePages.length - 1, {
          align: "center",
        });
      }
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      pageCount,
      virtualizer,
      disableHotkeys,
      selectedPages,
      isSelectingMoveTarget,
      visiblePageNumbers,
    ],
  );

  // spacebar to toggle checkbox
  useHotkeys(
    "space",
    (e) => {
      e.preventDefault();
      if (focusedPage !== null) {
        if (isPageSelectionUnavailable(focusedPage)) {
          return;
        }
        const isCurrentlySelected = selectedPages.has(focusedPage);
        togglePageSelection(focusedPage, !isCurrentlySelected);
      }
    },
    {
      enabled:
        focusedPage !== null && !disableHotkeys && !isSelectingMoveTarget,
    },
    [
      focusedPage,
      selectedPages,
      disableHotkeys,
      togglePageSelection,
      isPageSelectionUnavailable,
      isSelectingMoveTarget,
    ],
  );
};
